"use client";

import { useEffect, useRef, type RefObject } from "react";

/* A popover that opens from a button — the top bar's rewards, notifications
   and account menus — closes the way every menu on the web closes: a click
   or tap anywhere outside it, or Escape. The button that opened it sits
   inside the ref'd element, so its own toggle click is never "outside".

   `pointerdown` rather than `click`, so the popover is gone before whatever
   was pressed reacts, and so a touch counts. */
export function useDismiss(ref: RefObject<HTMLElement | null>, open: boolean, onClose: () => void) {
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!open) {
      return;
    }

    const away = (event: PointerEvent) => {
      const root = ref.current;

      if (root && !root.contains(event.target as Node)) {
        onCloseRef.current();
      }
    };

    const key = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onCloseRef.current();
      }
    };

    document.addEventListener("pointerdown", away);
    document.addEventListener("keydown", key);

    return () => {
      document.removeEventListener("pointerdown", away);
      document.removeEventListener("keydown", key);
    };
  }, [open, ref]);
}
